import React, { useState, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ScatterChart, Scatter, ZAxis } from 'recharts';

interface CustomerProfitability {
  customerName: string;
  timeAllocation: number;
  budgetAllocation: number;
  profitabilityIndex: number;
  totalBudget: number;
  monthlyBudget: number;
  color?: string;
}

interface CustomerProfitabilityChartProps {
  data: CustomerProfitability[];
  formatBudget: (value: number) => string;
}

type ChartType = 'bar' | 'scatter';

export function CustomerProfitabilityChart({ data, formatBudget }: CustomerProfitabilityChartProps) {
  const [chartType, setChartType] = useState<ChartType>('bar');
  const sortedData = [...data].sort((a, b) => b.profitabilityIndex - a.profitabilityIndex);

  // Calculate relative sizes for scatter plot
  const scatterData = useMemo(() => {
    const maxBudget = Math.max(...data.map(d => d.totalBudget));
    const minSize = 50; // Minimum plot size
    const maxSize = 1000; // Maximum plot size

    return data.map(item => ({
      ...item,
      // Calculate size proportionally between minSize and maxSize
      size: (item.totalBudget / maxBudget) * (maxSize - minSize) + minSize
    }));
  }, [data]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
          <p className="font-medium text-gray-900 mb-2">{data.customerName}</p>
          <div className="space-y-1 text-sm">
            <p>Time Allocation: {Math.round(data.timeAllocation * 100)}%</p>
            <p>Budget Allocation: {Math.round(data.budgetAllocation * 100)}%</p>
            <p>Total Budget: {formatBudget(data.totalBudget)}</p>
            <p>Budget per Month: {formatBudget(data.monthlyBudget)}</p>
            <p className="font-medium text-indigo-600">
              Profitability Index: {data.profitabilityIndex.toFixed(2)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={sortedData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="customerName"
          tick={{ fill: '#6B7280', fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={80}
          interval={0}
        />
        <YAxis
          tickFormatter={(value) => `${Math.round(value * 100)}%`}
          domain={[0, 'auto']}
          tick={{ fill: '#6B7280', fontSize: 12 }}
        />
        <Tooltip
          formatter={(value: number, name: string) => {
            if (name === 'Time Allocation' || name === 'Budget Allocation') {
              return [`${Math.round(value * 100)}%`, name];
            }
            return [value.toFixed(2), name];
          }}
          contentStyle={{
            backgroundColor: 'white',
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        />
        <Bar
          dataKey="timeAllocation"
          name="Time Allocation"
          fill="#93C5FD"
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="budgetAllocation"
          name="Budget Allocation"
          fill="#818CF8"
          radius={[4, 4, 0, 0]}
        />
        <ReferenceLine
          y={0.1}
          stroke="#D1D5DB"
          strokeDasharray="3 3"
          label={{
            value: "10% Threshold",
            position: "right",
            fill: "#6B7280",
            fontSize: 12
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderScatterChart = () => (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timeAllocation"
          type="number"
          name="Time Allocation"
          tickFormatter={(value) => `${Math.round(value * 100)}%`}
          domain={[0, 'auto']}
          label={{
            value: 'Time Allocation',
            position: 'bottom',
            offset: 0,
            style: { fill: '#6B7280' }
          }}
        />
        <YAxis
          dataKey="budgetAllocation"
          type="number"
          name="Budget Allocation"
          tickFormatter={(value) => `${Math.round(value * 100)}%`}
          domain={[0, 'auto']}
          label={{
            value: 'Budget Allocation',
            angle: -90,
            position: 'left',
            offset: 0,
            style: { fill: '#6B7280' }
          }}
        />
        <ZAxis
          dataKey="size"
          type="number"
          range={[50, 1000]}
          name="Total Budget"
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine
          y={0.1}
          stroke="#D1D5DB"
          strokeDasharray="3 3"
          label={{
            value: "10% Threshold",
            position: "right",
            fill: "#6B7280",
            fontSize: 12
          }}
        />
        <ReferenceLine
          segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
          stroke="#D1D5DB"
          strokeDasharray="3 3"
          label={{
            value: "1:1 Ratio",
            position: "right",
            fill: "#6B7280",
            fontSize: 12
          }}
        />
        <Scatter
          data={scatterData}
          fill="#818CF8"
          fillOpacity={0.6}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <TrendingUp className="w-5 h-5 text-indigo-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-800">Customer Profitability Analysis</h2>
        </div>
        <select
          value={chartType}
          onChange={(e) => setChartType(e.target.value as ChartType)}
          className="ml-4 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="bar">Bar Chart</option>
          <option value="scatter">Scatter Plot</option>
        </select>
      </div>
      <div className="h-160">
        {chartType === 'bar' ? renderBarChart() : renderScatterChart()}
      </div>
      <div className="mt-4 text-sm text-gray-600">
        {chartType === 'bar' ? (
          <p>Comparing time allocation vs budget allocation for each customer. Higher budget allocation relative to time spent indicates better profitability.</p>
        ) : (
          <p>Bubble size indicates total budget. Points above the 1:1 line indicate higher profitability (more budget than time allocation).</p>
        )}
      </div>
    </div>
  );
}